/**
 * Canvas 2D rendering helpers with retina support.
 */

/**
 * Set up a canvas element for retina-aware rendering.
 * Returns the 2D context scaled appropriately.
 */
export function setupCanvas(
  canvas: HTMLCanvasElement,
  width: number,
  height: number
): CanvasRenderingContext2D {
  const dpr = window.devicePixelRatio || 1;

  canvas.width = width * dpr;
  canvas.height = height * dpr;
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;

  const ctx = canvas.getContext('2d')!;
  ctx.scale(dpr, dpr);
  return ctx;
}

/**
 * Draw a rounded rectangle (useful for bar charts).
 */
export function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius = 4
) {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

/**
 * Draw grid lines on a canvas.
 */
export function drawGridLines(
  ctx: CanvasRenderingContext2D,
  ticks: number[],
  scaleY: (v: number) => number,
  xStart: number,
  xEnd: number,
  color: string
) {
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  ctx.setLineDash([]);
  for (const tick of ticks) {
    const y = Math.round(scaleY(tick)) + 0.5;
    ctx.beginPath();
    ctx.moveTo(xStart, y);
    ctx.lineTo(xEnd, y);
    ctx.stroke();
  }
}

/**
 * Draw axis labels on a canvas.
 */
export function drawYAxisLabels(
  ctx: CanvasRenderingContext2D,
  ticks: number[],
  scaleY: (v: number) => number,
  x: number,
  color: string,
  formatFn: (v: number) => string
) {
  ctx.fillStyle = color;
  ctx.font = '11px system-ui, sans-serif';
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';
  for (const tick of ticks) {
    ctx.fillText(formatFn(tick), x, scaleY(tick));
  }
}

/**
 * Draw X axis labels on a canvas (supports rotation and label thinning).
 *
 * When `maxLabels` is provided and the number of labels exceeds it, labels
 * are evenly thinned (skipped) to prevent overlap — the first and last labels
 * are always drawn.
 */
export function drawXAxisLabels(
  ctx: CanvasRenderingContext2D,
  labels: string[],
  positions: number[],
  y: number,
  color: string,
  rotate = false,
  maxLabels?: number
) {
  ctx.fillStyle = color;
  ctx.font = '11px system-ui, sans-serif';

  // Determine step to thin labels when too many for the available space
  const step =
    maxLabels && maxLabels > 0 && labels.length > maxLabels
      ? Math.ceil(labels.length / maxLabels)
      : 1;

  for (let i = 0; i < labels.length; i++) {
    // Skip labels that would overlap (always show first and last)
    if (step > 1 && i % step !== 0 && i !== labels.length - 1) continue;

    const x = positions[i]!;
    const label = labels[i]!;

    ctx.save();
    if (rotate) {
      ctx.translate(x, y);
      ctx.rotate(-Math.PI / 4);
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillText(label.length > 28 ? label.slice(0, 26) + '\u2026' : label, 0, 0);
    } else {
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText(label.length > 20 ? label.slice(0, 18) + '\u2026' : label, x, y);
    }
    ctx.restore();
  }
}
